import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Trophy, Clock, FileDown, Dices, Printer } from "lucide-react";
import { CalendarClock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { PlanLimitBadge } from "@/components/app/PlanLimitBadge";
import { GameDnaButton } from "@/components/app/GameDnaButton";
import { ExportGamesDialog } from "@/components/app/ExportGamesDialog";
import { AnalyzeGameDialog } from "@/components/app/AnalyzeGameDialog";
import { LOTTERIES, type LotterySlug } from "@/hooks/use-lottery-selection";
import { formatBRL } from "@/lib/utils";
import { ticketPrice } from "@/lib/pool-math";
import { CheckScopeDialog } from "@/components/app/CheckScopeDialog";
import { describeSavedGameScope, getActiveSpecials, type SavedGameScope, type SpecialDraw } from "@/lib/check-scope";

type SavedGame = {
  id: string;
  label: string | null;
  numbers: number[];
  cost: number;
  created_at: string;
  lottery_id: string;
  check_scope: SavedGameScope | null;
  check_from_contest: number | null;
  check_until_contest: number | null;
  special_schedule_id: string | null;
  lotteries: { name: string; slug: string } | null;
};

type ResultSummary = {
  saved_game_id: string;
  total_checks: number;
  best_hits: number;
  prize_count: number;
  last_contest: number | null;
};

const Jogos = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [games, setGames] = useState<SavedGame[]>([]);
  const [results, setResults] = useState<Record<string, ResultSummary>>({});
  const [filter, setFilter] = useState<"all" | "prize" | "pending">("all");
  const [lotteryFilter, setLotteryFilter] = useState<"all" | LotterySlug>("all");
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [specials, setSpecials] = useState<Record<string, SpecialDraw>>({});

  useEffect(() => {
    getActiveSpecials().then((list) => {
      const map: Record<string, SpecialDraw> = {};
      list.forEach((s) => { map[s.id] = s; });
      setSpecials(map);
    });
  }, []);

  /**
   * Calcula o preço de uma aposta usando a tabela oficial (Painel Admin),
   * ignorando qualquer valor antigo armazenado em `saved_games.cost`.
   */
  const computeCost = (g: SavedGame): number => {
    const slug = g.lotteries?.slug as LotterySlug | undefined;
    if (!slug) return Number(g.cost) || 0;
    return ticketPrice(slug, g.numbers.length);
  };

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data: g } = await supabase
      .from("saved_games")
      .select("id, label, numbers, cost, created_at, lottery_id, check_scope, check_from_contest, check_until_contest, special_schedule_id, lotteries(name, slug)")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    setGames((g ?? []) as SavedGame[]);

    const { data: r } = await supabase
      .from("game_results")
      .select("saved_game_id, hits, is_prize, contest_number")
      .eq("user_id", user.id);

    const summary: Record<string, ResultSummary> = {};
    (r ?? []).forEach((row) => {
      const s = summary[row.saved_game_id] ?? {
        saved_game_id: row.saved_game_id,
        total_checks: 0,
        best_hits: 0,
        prize_count: 0,
        last_contest: null,
      };
      s.total_checks += 1;
      s.best_hits = Math.max(s.best_hits, row.hits);
      if (row.is_prize) s.prize_count += 1;
      s.last_contest = Math.max(s.last_contest ?? 0, row.contest_number);
      summary[row.saved_game_id] = s;
    });
    setResults(summary);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [user]);

  const remove = async (id: string) => {
    const { error } = await supabase.from("saved_games").delete().eq("id", id);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
      return;
    }
    setGames((p) => p.filter((g) => g.id !== id));
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
    toast({ title: "Jogo excluído" });
  };

  const filtered = games.filter((g) => {
    const r = results[g.id];
    if (filter === "prize") return r && r.prize_count > 0;
    if (filter === "pending") return !r || r.total_checks === 0;
    return true;
  }).filter((g) => lotteryFilter === "all" || g.lotteries?.slug === lotteryFilter);

  const countsBySlug = useMemo(() => {
    const c: Record<string, number> = {};
    games.forEach((g) => {
      const s = g.lotteries?.slug;
      if (s) c[s] = (c[s] ?? 0) + 1;
    });
    return c;
  }, [games]);

  const groupedFiltered = useMemo(() => {
    if (lotteryFilter !== "all") return null;
    const groups = new Map<string, { name: string; slug: string; items: SavedGame[] }>();
    LOTTERIES.forEach((l) => {
      const items = filtered.filter((g) => g.lotteries?.slug === l.slug);
      if (items.length) groups.set(l.slug, { name: l.name, slug: l.slug, items });
    });
    // jogos sem loteria conhecida
    const orphans = filtered.filter((g) => !g.lotteries?.slug || !LOTTERIES.find((l) => l.slug === g.lotteries?.slug));
    if (orphans.length) groups.set("outros", { name: "Outros", slug: "outros", items: orphans });
    return Array.from(groups.values());
  }, [filtered, lotteryFilter]);

  const filteredIds = useMemo(() => filtered.map((g) => g.id), [filtered]);
  const selectedVisibleCount = filteredIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = filteredIds.length > 0 && selectedVisibleCount === filteredIds.length;
  const selectedGames = useMemo(
    () => games.filter((g) => selectedIds.has(g.id) && g.lotteries?.slug),
    [games, selectedIds],
  );
  const selectedLotterySlugs = useMemo(
    () => Array.from(new Set(selectedGames.map((g) => g.lotteries!.slug))),
    [selectedGames],
  );
  const selectedCostPerGame = useMemo(() => {
    if (selectedGames.length === 0) return undefined;
    const first = computeCost(selectedGames[0]);
    return selectedGames.every((g) => computeCost(g) === first) ? first : undefined;
  }, [selectedGames]);
  const printableSelection = selectedLotterySlugs.length === 1 && selectedGames[0]?.lotteries
    ? {
        slug: selectedLotterySlugs[0] as "mega-sena" | "lotofacil" | "quina" | "dupla-sena",
        name: selectedGames[0].lotteries.name,
        games: selectedGames.map((g) => g.numbers),
      }
    : null;

  // Slug comum para o diálogo de escopo (precisa que a seleção seja de uma única loteria)
  const scopeDialogSlug = selectedLotterySlugs.length === 1 ? selectedLotterySlugs[0] : null;
  const scopeDialogName = selectedLotterySlugs.length === 1 ? selectedGames[0]?.lotteries?.name : undefined;

  // Agrupa os jogos selecionados por loteria para gerar a lista (suporta multi-loteria)
  const selectionGroups = useMemo(() => {
    const map = new Map<string, { lotteryName: string; games: number[][]; costPerGame?: number; costs: number[] }>();
    selectedGames.forEach((g) => {
      const slug = g.lotteries!.slug;
      const name = g.lotteries!.name;
      const grp = map.get(slug) ?? { lotteryName: name, games: [], costPerGame: undefined, costs: [] };
      grp.games.push(g.numbers);
      grp.costs.push(computeCost(g));
      map.set(slug, grp);
    });
    return Array.from(map.values()).map((grp) => {
      const first = grp.costs[0];
      const allEqual = grp.costs.every((c) => c === first);
      return {
        lotteryName: grp.lotteryName,
        games: grp.games,
        costPerGame: allEqual ? first : undefined,
      };
    });
  }, [selectedGames]);

  const toggleOne = (id: string) =>
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) {
        filteredIds.forEach((id) => n.delete(id));
      } else {
        filteredIds.forEach((id) => n.add(id));
      }
      return n;
    });
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Excluir ${ids.length} jogo(s) selecionado(s)?`)) return;
    setBulkDeleting(true);
    const { error } = await supabase.from("saved_games").delete().in("id", ids);
    setBulkDeleting(false);
    if (error) {
      toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
      return;
    }
    setGames((p) => p.filter((g) => !selectedIds.has(g.id)));
    setSelectedIds(new Set());
    toast({ title: `${ids.length} jogo(s) excluído(s)` });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-serif text-3xl text-foreground">Meus jogos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Conferência automática contra todos os concursos. Você é avisado assim que premia.
          </p>
        </div>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
          <TabsList>
            <TabsTrigger value="all">Todos ({games.length})</TabsTrigger>
            <TabsTrigger value="prize">Premiados</TabsTrigger>
            <TabsTrigger value="pending">Pendentes</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <PlanLimitBadge resource="saved_games" label="Jogos salvos" />

      <Tabs value={lotteryFilter} onValueChange={(v) => setLotteryFilter(v as typeof lotteryFilter)}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="all">Todas ({games.length})</TabsTrigger>
          {LOTTERIES.map((l) => (
            <TabsTrigger key={l.slug} value={l.slug}>
              {l.name} ({countsBySlug[l.slug] ?? 0})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-lt-border bg-card px-3 py-2">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={allVisibleSelected}
              onCheckedChange={toggleAllVisible}
              aria-label="Selecionar todos"
            />
            {allVisibleSelected ? "Desmarcar todos" : "Selecionar todos"}
            {selectedIds.size > 0 && (
              <span className="ml-2 text-foreground">
                {selectedIds.size} selecionado{selectedIds.size > 1 ? "s" : ""}
              </span>
            )}
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <ExportGamesDialog
              lotterySlug={printableSelection?.slug ?? "mega-sena"}
              lotteryName={printableSelection?.name ?? "Jogos selecionados"}
              games={printableSelection?.games ?? (selectedGames[0]?.numbers ? [selectedGames[0].numbers] : [])}
              groups={selectionGroups}
              costPerGame={selectedCostPerGame}
              filenameBase={`jogos-selecionados-${printableSelection?.slug ?? "loteria"}`}
              disabled={selectedGames.length === 0}
              trigger={
                <Button size="sm" variant="outline" disabled={selectedGames.length === 0}>
                  <Printer className="mr-1.5 h-3.5 w-3.5" />
                  Imprimir selecionados
                </Button>
              }
            />
            <Button
              size="sm"
              variant="destructive"
              disabled={selectedIds.size === 0 || bulkDeleting}
              onClick={deleteSelected}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              {bulkDeleting ? "Excluindo…" : "Excluir selecionados"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!scopeDialogSlug}
              onClick={() => setScopeDialogOpen(true)}
              title={scopeDialogSlug ? undefined : "Selecione jogos de uma única loteria"}
            >
              <CalendarClock className="mr-1.5 h-3.5 w-3.5" />
              Alterar conferência
            </Button>
          </div>
        </div>
      )}

      {scopeDialogSlug && (
        <CheckScopeDialog
          open={scopeDialogOpen}
          onOpenChange={setScopeDialogOpen}
          lotterySlug={scopeDialogSlug}
          lotteryName={scopeDialogName}
          title="Alterar conferência dos jogos selecionados"
          confirmLabel="Atualizar conferência"
          description="Define como o conferidor avaliará os jogos selecionados. Resultados fora da nova janela serão removidos."
          onConfirm={async ({ scope, from, until, specialScheduleId }) => {
            const ids = Array.from(selectedIds);
            const { error } = await supabase.rpc("update_saved_games_check_scope", {
              _game_ids: ids,
              _scope: scope,
              _from: from,
              _until: until,
              _special_schedule_id: specialScheduleId ?? undefined,
            });
            if (error) {
              toast({ title: "Erro", description: error.message, variant: "destructive" });
              return;
            }
            toast({ title: "Conferência atualizada" });
            setSelectedIds(new Set());
            await load();
          }}
        />
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : filtered.length === 0 ? (
        <Card className="border-lt-border bg-card">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Dices className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {games.length === 0 ? "Você ainda não salvou nenhum jogo." : "Nenhum jogo neste filtro."}
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/gerador">Gerar jogos</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        (() => {
          const renderCard = (g: SavedGame) => {
            const r = results[g.id];
            const isPrize = r && r.prize_count > 0;
            const isSelected = selectedIds.has(g.id);
            return (
              <Card
                key={g.id}
                className={`border-lt-border bg-card ${isPrize ? "border-primary/60" : ""} ${isSelected ? "ring-1 ring-primary/40" : ""}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleOne(g.id)}
                        className="mt-1"
                        aria-label="Selecionar jogo"
                      />
                      <div>
                        <CardTitle className="font-serif text-lg break-words">
                          {g.label || "Jogo sem nome"}
                        </CardTitle>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {g.lotteries?.name} • {formatBRL(computeCost(g))} •{" "}
                          {new Date(g.created_at).toLocaleDateString("pt-BR")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isPrize ? (
                        <Badge className="gap-1 bg-primary text-primary-foreground">
                          <Trophy className="h-3 w-3" />
                          {r.prize_count} prêmio{r.prize_count > 1 ? "s" : ""}
                        </Badge>
                      ) : r ? (
                        <Badge variant="outline" className="gap-1">
                          <Clock className="h-3 w-3" /> Conferido
                        </Badge>
                      ) : (
                        <Badge variant="outline">Pendente</Badge>
                      )}
                      {g.lotteries?.slug && (
                        <GameDnaButton
                          numbers={g.numbers}
                          lotterySlug={g.lotteries.slug as LotterySlug}
                          fromLabel="Voltar para meus jogos"
                        />
                      )}
                      {g.lotteries?.slug && (
                        <AnalyzeGameDialog
                          numbers={g.numbers}
                          lotterySlug={g.lotteries.slug as LotterySlug}
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => remove(g.id)}
                        aria-label="Excluir"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Badge
                      variant="outline"
                      className={`gap-1 text-[10px] ${g.check_scope === "special" ? "border-primary/60 bg-primary/10 text-primary" : ""}`}
                    >
                      <CalendarClock className="h-3 w-3" />
                      Conferindo: {describeSavedGameScope(
                        g.check_scope,
                        g.check_from_contest,
                        g.check_until_contest,
                        g.special_schedule_id ? specials[g.special_schedule_id]?.special_name : null,
                      )}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {g.numbers.map((n) => (
                      <span
                        key={n}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-lt-border bg-background text-xs font-medium text-foreground"
                      >
                        {String(n).padStart(2, "0")}
                      </span>
                    ))}
                  </div>
                  {r && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Melhor resultado: <span className="text-foreground">{r.best_hits} acertos</span>{" "}
                      em {r.total_checks} concursos conferidos
                      {r.last_contest != null && (
                        <> · último conferido: <span className="text-foreground">#{r.last_contest}</span></>
                      )}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          };
          if (groupedFiltered) {
            return (
              <div className="space-y-6">
                {groupedFiltered.map((grp) => (
                  <section key={grp.slug} className="space-y-3">
                    <div className="flex items-center gap-2 border-b border-lt-border pb-2">
                      <h2 className="font-serif text-xl text-foreground">{grp.name}</h2>
                      <Badge variant="outline" className="text-xs">
                        {grp.items.length} jogo{grp.items.length > 1 ? "s" : ""}
                      </Badge>
                    </div>
                    <div className="grid gap-3">{grp.items.map(renderCard)}</div>
                  </section>
                ))}
              </div>
            );
          }
          return <div className="grid gap-3">{filtered.map(renderCard)}</div>;
        })()
      )}

      <div className="flex justify-end">
        <Button asChild variant="outline" size="sm">
          <Link to="/app/exportar">
            <FileDown className="mr-2 h-3.5 w-3.5" />
            Exportar volantes
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default Jogos;